/* Pure lifecycle checks. Fetching a source never approves a rule or changes a value. */
const parseDate = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
  const date = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(date) && new Date(date).toISOString().slice(0, 10) === value ? date : NaN;
};

const inspectRegister = (register, asOf) => {
  const now = parseDate(asOf);
  if (!Number.isFinite(now)) throw new Error("A valid explicit as-of date is required");
  const issues = [];
  const ids = new Set();
  register.groups.forEach((rule) => {
    const add = (code) => issues.push({ rule: rule.id, code });
    if (ids.has(rule.id)) add("duplicate-id");
    ids.add(rule.id);
    ["effectiveFrom", "reviewedOn", "reviewDue"].forEach((field) => {
      if (!Number.isFinite(parseDate(rule[field]))) add(`invalid-${field}`);
    });
    if (rule.effectiveUntil && !Number.isFinite(parseDate(rule.effectiveUntil))) add("invalid-effectiveUntil");
    if (!rule.sources.length || rule.sources.some((id) => !register.sources[id])) add("missing-source");
    if (parseDate(rule.reviewedOn) > now) add("future-review-date");
    if (parseDate(rule.reviewDue) <= parseDate(rule.reviewedOn)) add("invalid-review-window");
    if (parseDate(rule.reviewDue) <= now) add("review-overdue");
    if (parseDate(rule.effectiveFrom) > now) add("not-yet-effective");
    if (rule.effectiveUntil && parseDate(rule.effectiveUntil) <= now) add("snapshot-expired");
  });
  return issues;
};

// A future or unreviewed candidate is never selected as the active version.
// Engines still use the explicitly released figures; this helper is for versioned adapters.
const selectApplicableVersion = (versions, asOf) => {
  const date = parseDate(asOf);
  if (!Number.isFinite(date)) throw new Error("A valid explicit as-of date is required");
  return versions.filter((version) => version.status === "released" &&
    parseDate(version.reviewedOn) <= date && parseDate(version.effectiveFrom) <= date &&
    (!version.effectiveUntil || date < parseDate(version.effectiveUntil)))
    .sort((a, b) => parseDate(b.effectiveFrom) - parseDate(a.effectiveFrom))[0] || null;
};

const leafPaths = (value, prefix = "") => {
  if (value !== null && typeof value === "object") {
    return Object.entries(value).flatMap(([key, child]) => leafPaths(child, prefix ? `${prefix}.${key}` : key));
  }
  return [prefix];
};

const ruleForPath = (register, fieldPath) => register.groups.filter((rule) =>
  rule.paths.some((prefix) => fieldPath === prefix || fieldPath.startsWith(`${prefix}.`)));

module.exports = { parseDate, inspectRegister, selectApplicableVersion, leafPaths, ruleForPath };
